<template>
    <PanelPage>
        <template v-slot:title>
            <div v-if="meta.adverts.length > 0">
                <div class="flex md:gap-6 gap-2 justify-center items-center">
                    <div v-for="advert in meta.adverts" :key="advert.id" class="w-1/3">
                        <a :href="advert.url" target="_blank" rel="noopener noreferrer">
                            <img :src="advert.image_path" :alt="advert.name" class="w-full" />
                        </a>
                    </div>
                </div>
            </div>
            <SmartBreadcrumbs
                type="InertiaLink"
                class="mt-3"
                :pages="[
                    {
                        name: $tc('messages.activities', 2),
                        href: null,
                    },
                    {
                        name: $t('messages.practice'),
                        href: null,
                    },
                ]"
            />
        </template>

        <div class="flex flex-wrap gap-4 mt-3 items-center">
            <div>
                <div>
                    {{ $tc('messages.stages', 1) }}
                </div>
                <NavLinks :links="meta.nav_links.stages" />
            </div>

            <div>
                <div>
                    {{ $tc('messages.grade_levels', 1) }}
                </div>
                <NavLinks :links="meta.nav_links.grade_levels" />
            </div>

            <div class="flex flex-wrap gap-x-2 mt-5">
                <AppButton
                    element="InertiaLink"
                    :href="subject.url"
                    v-for="subject in subjects"
                    :key="subject.id"
                    class="flex gap-x-2"
                >
                    {{ subject.text }}

                    <IconCircleCheckFilled class="h-5 w-5 text-indigo-600" v-if="filters.subject_id == subject.id" />
                </AppButton>
            </div>

            <div v-if="!!form.subject_id">
                <FormLabel :value="$t('messages.difficulty')" for="difficulty" />
                <div class="flex gap-4">
                    <InertiaLink
                        v-for="link in meta.links.difficulties"
                        :key="link.text"
                        :href="link.url"
                        class="rounded-full flex items-center justify-center cursor-pointer focus:outline-none ring-indigo-500"
                    >
                        <span
                            aria-hidden="true"
                            class="w-6 h-6 border border-black border-opacity-10 rounded-full tabular-nums text-xs flex justify-center align-middle items-center"
                            :class="{
                                'bg-red-400 text-red-800 border-red-800 border-2':
                                    link.text == filters.difficulty && link.text == 1,
                                'bg-orange-400 text-orange-800 border-orange-800 border-2':
                                    link.text == filters.difficulty && link.text == 2,
                                'bg-yellow-400 text-yellow-800 border-yellow-800 border-2':
                                    link.text == filters.difficulty && link.text == 3,
                                'bg-blue-400 text-blue-800 border-blue-800 border-2':
                                    link.text == filters.difficulty && link.text == 4,
                                'bg-green-400 text-green-800 border-green-800 border-2':
                                    link.text == filters.difficulty && link.text == 5,
                                'bg-gray-200 text-gray-400': link.text != filters.difficulty,
                            }"
                        >
                            {{ link.text }}
                        </span>
                    </InertiaLink>
                </div>
                <FormError :value="form.errors.difficulty" class="mt-2" />
            </div>
        </div>

        <div v-if="!!form.subject_id">
            <div v-if="contentBlocks.length" class="mt-5">
                <AppWell class="mt-5">
                    <div class="md:grid md:grid-cols-2 lg:grid-cols-3 md:gap-6 mt-3">
                        <div v-for="block in filteredContentBlocks" :key="block.id">
                            <AppCard>
                                <div class="p-1">
                                    <div class="font-bold">
                                        {{ block.name }}
                                    </div>
                                    <div class="pl-3 divide-y divide-gray-300">
                                        <div class="mt-3" v-for="section in block.sections" :key="section.name">
                                            <div class="mt-3">
                                                {{ section.name }}
                                            </div>
                                            <div class="pl-3">
                                                <div
                                                    v-for="tag in section.tags"
                                                    :key="tag.id"
                                                    class="mt-1 flex items-center gap-x-2"
                                                >
                                                    <div>
                                                        <!--                                                        <MedallionLevelsInfo :levels-info="tag.levels_info" />-->
                                                        <GuestMedallionLevelsInfo :tag="tag" />
                                                    </div>
                                                    <AppButton
                                                        element="button"
                                                        type="button"
                                                        v-if="tag.url === null"
                                                        :title="tag.description"
                                                        disabled
                                                    >
                                                        {{ tag.name }}
                                                    </AppButton>
                                                    <AppButton
                                                        v-else
                                                        element="InertiaLink"
                                                        :href="tag.url"
                                                        :title="tag.description"
                                                        :desired-action="tag.desired_action"
                                                    >
                                                        {{ tag.name }}
                                                    </AppButton>
                                                    <div v-if="tag.links.length">
                                                        <TagHelpSlideOver :tag="tag" />
                                                    </div>
                                                    <div v-if="tag.expected_level">
                                                        <Tippy>
                                                            <button>
                                                                <BadgePill color="indigo">
                                                                    {{ tag.expected_level }}
                                                                </BadgePill>
                                                            </button>
                                                            <template #content>
                                                                <span>
                                                                    {{
                                                                        $t('messages.expected_level_X_indicator', {
                                                                            level: tag.expected_level,
                                                                        })
                                                                    }}
                                                                </span>
                                                            </template>
                                                        </Tippy>
                                                    </div>
                                                    <IconSpeedboat class="text-indigo-500 h-5 w-5" v-if="tag.in_race" />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </AppCard>
                        </div>
                    </div>
                </AppWell>
            </div>
            <EmptyState v-else-if="!!filters.difficulty" picture />
        </div>
    </PanelPage>
</template>

<script>
import PanelPage from '@/Shared/PanelPage.vue';
import EmptyState from '@/components/EmptyState.vue';
import AppButton from '@/components/AppButton.vue';
import FormLabel from '@/components/Forms/FormLabel.vue';
import FormError from '@/components/Forms/FormError.vue';
import { IconCircleCheckFilled, IconSpeedboat } from '@tabler/icons-vue';
import AppCard from '@/Shared/AppCard.vue';
import AppWell from '@/Shared/AppWell.vue';
import AlertMessage from '@/components/AlertMessage.vue';
import CountdownTimer from '@/components/CountdownTimer.vue';
import MedallionLevelsInfo from '@/components/MedallionLevelsInfo.vue';
import NavLinks from '@/components/NavLinks.vue';
import TagHelpSlideOver from '@/Pages/Student/Activity/Practice/partials/TagHelpSlideOver.vue';
import { Tippy } from 'vue-tippy';
import BadgePill from '@/components/BadgePill.vue';
import LandingLayout from '@/Shared/LandingLayout.vue';
import GuestMedallionLevelsInfo from './partials/GuestMedallionLevelsInfo.vue';

export default {
    components: {
        GuestMedallionLevelsInfo,
        BadgePill,
        Tippy,
        TagHelpSlideOver,
        NavLinks,
        MedallionLevelsInfo,
        CountdownTimer,
        AlertMessage,
        AppWell,
        AppCard,
        FormError,
        FormLabel,
        AppButton,
        PanelPage,
        EmptyState,
        IconCircleCheckFilled,
        IconSpeedboat,
    },
    layout: LandingLayout,
    name: 'Index',
    props: ['subjects', 'contentBlocks', 'filters', 'meta'],
    data() {
        return {
            form: this.$inertia.form({
                subject_id: this.filters.subject_id,
                difficulty: this.filters.difficulty,
            }),
            showAll: false,
        };
    },
    computed: {
        filteredContentBlocks() {
            return this.contentBlocks;
        },
    },
};
</script>
